/**
 * Push Notification Helper
 * Handles Firebase Cloud Messaging (FCM) for push notifications
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

let messaging = null;

// Initialize messaging only if supported
try {
  if ('Notification' in window && 'serviceWorker' in navigator) {
    messaging = getMessaging();
  }
} catch (error) {
  console.log('Firebase Messaging not supported:', error);
}

/**
 * Request notification permission and get FCM token
 */
export async function requestNotificationPermission(userId) {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return { success: false, error: 'Notifications not supported' };
    }

    // Check if already granted
    if (Notification.permission === 'granted') {
      return await setupNotifications(userId);
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('✅ Notification permission granted');
      return await setupNotifications(userId);
    } else {
      console.log('❌ Notification permission denied');
      return { success: false, error: 'Permission denied' };
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Set up notifications after permission is granted
 */
async function setupNotifications(userId) {
  try {
    if (!messaging) {
      throw new Error('Messaging not initialized');
    }

    // Get FCM token
    // IMPORTANT: You must add your VAPID key from Firebase Console
    // 1. Go to https://console.firebase.google.com/
    // 2. Select your project
    // 3. Settings (⚙️) → Project Settings → Cloud Messaging
    // 4. Under "Web Push certificates", click "Generate key pair"
    // 5. Copy the key and add it to your .env as REACT_APP_FIREBASE_VAPID_KEY
    
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    
    if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY_HERE') {
      throw new Error('VAPID key not configured. Please add REACT_APP_FIREBASE_VAPID_KEY to your environment variables.');
    }
    
    const token = await getToken(messaging, {
      vapidKey: vapidKey
    });

    if (token) {
      console.log('📱 FCM Token:', token);
      
      // Save token to user's Firestore document
      if (userId) {
        await updateDoc(doc(db, 'users', userId), {
          fcmToken: token,
          notificationsEnabled: true,
          notificationsUpdatedAt: new Date().toISOString()
        });
        console.log('✅ FCM token saved to Firestore');
      }

      return { success: true, token };
    } else {
      throw new Error('No FCM token received');
    }
  } catch (error) {
    console.error('Error setting up notifications:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Listen for foreground messages
 */
export function onForegroundMessage(callback) {
  if (!messaging) {
    console.log('Messaging not available');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    
    const notificationTitle = payload.notification?.title || 'Family Hub';
    const notificationOptions = {
      body: payload.notification?.body || '',
      icon: '/logo192.png',
      badge: '/logo192.png'
    };

    // Show notification if browser supports it
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notificationTitle, notificationOptions);
    }

    // Call callback with payload
    if (callback) {
      callback(payload);
    }
  });
}

/**
 * Update app badge (shows number on app icon)
 */
export function updateAppBadge(count) {
  if ('setAppBadge' in navigator) {
    if (count > 0) {
      navigator.setAppBadge(count).catch(err => {
        console.log('Failed to set app badge:', err);
      });
    } else {
      navigator.clearAppBadge().catch(err => {
        console.log('Failed to clear app badge:', err);
      });
    }
  }
}

/**
 * Check if notifications are enabled
 */
export function areNotificationsEnabled() {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Disable notifications for a user
 */
export async function disableNotifications(userId) {
  try {
    if (userId) {
      await updateDoc(doc(db, 'users', userId), {
        notificationsEnabled: false,
        notificationsUpdatedAt: new Date().toISOString()
      });
      console.log('✅ Notifications disabled');
    }
    return { success: true };
  } catch (error) {
    console.error('Error disabling notifications:', error);
    return { success: false, error: error.message };
  }
}

